/**
 * Template utility for loading and cloning HTML templates
 */

export class TemplateLoader {
  /**
   * Load a template by ID and return a cloned DocumentFragment
   */
  static loadTemplate(templateId: string): DocumentFragment {
    const template = document.getElementById(templateId) as HTMLTemplateElement;

    if (!template) {
      throw new Error(`Template with ID "${templateId}" not found`);
    }

    if (!(template instanceof HTMLTemplateElement)) {
      throw new Error(`Element with ID "${templateId}" is not a template element`);
    }

    return template.content.cloneNode(true) as DocumentFragment;
  }

  /**
   * Load a template and return the root element
   */
  static loadTemplateElement(templateId: string): Element {
    const fragment = this.loadTemplate(templateId);
    const element = fragment.firstElementChild;

    if (!element) {
      throw new Error(`Template "${templateId}" does not contain any elements`);
    }

    return element;
  }

  /**
   * Load a template and bind data to elements with data-bind attributes
   */
  static loadTemplateWithData(templateId: string, data: Record<string, any>): Element {
    const element = this.loadTemplateElement(templateId);

    // Find all elements with data-bind attribute
    const bindElements = element.querySelectorAll('[data-bind]');

    bindElements.forEach((el) => {
      const bindKey = el.getAttribute('data-bind');
      if (bindKey && data[bindKey] !== undefined) {
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
          el.value = data[bindKey];
        } else {
          el.textContent = data[bindKey];
        }
      }
    });

    return element;
  }

  /**
   * Replace placeholders in template with actual values
   */
  static replacePlaceholders(element: Element, replacements: Record<string, string>): void {
    const html = element.innerHTML;
    let updatedHtml = html;

    Object.entries(replacements).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      updatedHtml = updatedHtml.replace(new RegExp(placeholder, 'g'), value);
    });

    element.innerHTML = updatedHtml;
  }
}